'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  getCurrentUserProfile, getAllUsers, getTransactionsByUser, updateUserProfile,
  addTransaction, formatCurrency, formatDate, WALLET_COMMISSION_RATE,
  getClaimedMilestones, claimMilestone,
} from '@/lib/supabaseService';
import { sendEmail } from '@/lib/sendEmail';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import type { User, Transaction } from '@/lib/supabaseService';
import { Copy, Check, Users, TrendingUp, IndianRupee, UserCheck, Gift, CheckCircle2, Sparkles } from 'lucide-react';
import { trackAffiliateReferralCopied, trackMilestoneClaimed } from '@/lib/analytics';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://dropservic9995.builtwithrocket.new';

interface Milestone { count: number; reward: number; label: string; }
const MILESTONES: Milestone[] = [
  { count: 5, reward: 500, label: '5 Referrals' },
  { count: 10, reward: 1500, label: '10 Referrals' },
  { count: 25, reward: 5000, label: '25 Referrals' },
];

interface ReferredAgent {
  id: string; fullName: string; email: string; phone: string;
  kycStatus: User['kycStatus']; totalOrders: number; dropserviceEarnings: number;
  createdAt: string; suspended: boolean;
}

export default function AffiliateClient() {
  const router = useRouter();
  const { user: authUser, loading: authLoading } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [referredAgents, setReferredAgents] = useState<ReferredAgent[]>([]);
  const [affiliateTxns, setAffiliateTxns] = useState<Transaction[]>([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claimedMilestones, setClaimedMilestones] = useState<number[]>([]);
  const [autoClaimingMilestone, setAutoClaimingMilestone] = useState<number | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) { router.replace('/sign-up-login-screen'); return; }
    const load = async () => {
      const [profile, allUsers] = await Promise.all([getCurrentUserProfile(), getAllUsers()]);
      if (!profile) { router.replace('/sign-up-login-screen'); return; }
      setUser(profile);
      const referred = allUsers.filter(u => u.referredBy === profile.referralCode && !u.isAdmin);
      setReferredAgents(referred.map(u => ({
        id: u.id, fullName: u.fullName, email: u.email, phone: u.phone,
        kycStatus: u.kycStatus, totalOrders: u.totalOrders, dropserviceEarnings: u.dropserviceEarnings,
        createdAt: u.createdAt, suspended: u.suspended,
      })));
      const [txns, claimed] = await Promise.all([
        getTransactionsByUser(profile.id),
        getClaimedMilestones(profile.id),
      ]);
      setAffiliateTxns(txns.filter(t => t.description.toLowerCase().includes('affiliate') || t.description.toLowerCase().includes('milestone reward')));
      setClaimedMilestones(claimed);

      // Auto-credit any unclaimed milestones the user has reached
      await autoCheckAndCreditMilestones(profile, referred.length, claimed);

      setLoading(false);
    };
    load();
  }, [authUser, authLoading, router]);

  // Auto-credit milestones without requiring admin or manual claim
  const autoCheckAndCreditMilestones = async (profile: User, referralCount: number, alreadyClaimed: number[]) => {
    for (const milestone of MILESTONES) {
      if (referralCount >= milestone.count && !alreadyClaimed.includes(milestone.count)) {
        setAutoClaimingMilestone(milestone.count);
        try {
          const fresh = await getCurrentUserProfile();
          if (!fresh) continue;
          // Double-check it's not already claimed (race condition guard)
          const freshClaimed = await getClaimedMilestones(fresh.id);
          if (freshClaimed.includes(milestone.count)) {
            setClaimedMilestones(prev => [...new Set([...prev, milestone.count])]);
            continue;
          }
          const newBalance = fresh.walletBalance + milestone.reward;
          await updateUserProfile(fresh.id, {
            walletBalance: newBalance,
            affiliateEarnings: fresh.affiliateEarnings + milestone.reward,
          });
          await addTransaction({
            userId: fresh.id,
            type: 'credit',
            amount: milestone.reward,
            description: `Milestone reward — ${milestone.label} referrals reached (auto-credited)`,
          });
          await claimMilestone(fresh.id, milestone.count);
          trackMilestoneClaimed(milestone.count, milestone.reward);
          setClaimedMilestones(prev => [...new Set([...prev, milestone.count])]);
          setUser(prev => prev ? { ...prev, walletBalance: newBalance } : prev);
          // Send email notification
          sendEmail({
            type: 'payout_notification',
            agentEmail: fresh.email,
            agentName: fresh.fullName,
            milestoneLabel: milestone.label,
            rewardAmount: milestone.reward,
            newWalletBalance: newBalance,
          });
        } catch (err) {
          console.error('Auto-milestone credit failed:', err);
        }
        setAutoClaimingMilestone(null);
      }
    }
  };

  const referralLink = user ? `${SITE_URL}/sign-up-login-screen?ref=${user.referralCode}` : '';

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (user) trackAffiliateReferralCopied(user.referralCode);
    });
  };

  const kycBadge = (status: User['kycStatus']) => {
    const map: Record<User['kycStatus'], { label: string; cls: string }> = {
      pending: { label: 'Pending', cls: 'bg-muted text-muted-foreground' },
      submitted: { label: 'Under Review', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
      approved: { label: 'Verified', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    };
    const { label, cls } = map[status];
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
  };

  const totalAffiliateEarnings = user?.affiliateEarnings ?? 0;
  const totalTeamOrders = referredAgents.reduce((s, a) => s + a.totalOrders, 0);
  const totalTeamRevenue = referredAgents.reduce((s, a) => s + a.dropserviceEarnings, 0);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout activeRoute="/affiliate">
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Affiliate Program</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Invite members using your referral link and earn bonuses for every active referral.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            { label: 'Total Earned', value: formatCurrency(totalAffiliateEarnings), icon: <IndianRupee size={16} className="text-primary" />, bg: 'bg-primary/10' },
            { label: 'Team Size', value: referredAgents.length, icon: <Users size={16} className="text-accent" />, bg: 'bg-accent/10' },
            { label: 'Team Orders', value: totalTeamOrders, icon: <TrendingUp size={16} className="text-green-500" />, bg: 'bg-green-500/10' },
            { label: 'Team Revenue', value: formatCurrency(totalTeamRevenue), icon: <UserCheck size={16} className="text-blue-500" />, bg: 'bg-blue-500/10' },
          ].map(stat => (
            <div key={stat.label} className="bg-card border border-border rounded-xl p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${stat.bg} flex items-center justify-center flex-shrink-0`}>{stat.icon}</div>
              <div className="min-w-0"><p className="text-[10px] sm:text-xs text-muted-foreground">{stat.label}</p><p className="text-base sm:text-lg font-bold text-foreground">{stat.value}</p></div>
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-foreground mb-1">Your Referral Link</h2>
          <p className="text-xs text-muted-foreground mb-3">Share this link with potential members. You earn a <span className="font-semibold text-foreground">₹750 registration bonus</span> when they join, plus <span className="font-semibold text-foreground">{(WALLET_COMMISSION_RATE * 100).toFixed(0)}% commission</span> every time they add funds to their wallet.</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-foreground font-mono truncate select-all min-h-[44px] flex items-center">{referralLink}</div>
            <button onClick={handleCopy} className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 flex-shrink-0 min-h-[44px] ${copied ? 'bg-green-500/10 text-green-600 border border-green-500/30' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
              {copied ? <Check size={15} /> : <Copy size={15} />}{copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Milestones — auto-credited, no manual claim needed */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-foreground">Milestone Rewards</h2>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full">
              <Sparkles size={10} /> Auto-credited
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Reach referral milestones to unlock bonus rewards. Rewards are automatically credited to your wallet!</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {MILESTONES.map(m => {
              const reached = referredAgents.length >= m.count;
              const claimed = claimedMilestones.includes(m.count);
              const isAutoClaiming = autoClaimingMilestone === m.count;
              return (
                <div key={m.count} className={`rounded-xl border p-4 text-center transition-all ${claimed ? 'bg-green-500/5 border-green-500/30' : reached ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-border'}`}>
                  <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${claimed ? 'bg-green-500/10' : reached ? 'bg-primary/10' : 'bg-muted'}`}>
                    {claimed ? <CheckCircle2 size={20} className="text-green-500" /> : <Gift size={20} className={reached ? 'text-primary' : 'text-muted-foreground'} />}
                  </div>
                  <p className="text-sm font-bold text-foreground">{m.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Reward: {formatCurrency(m.reward)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{referredAgents.length}/{m.count} referrals</p>
                  {claimed ? (
                    <span className="mt-2 inline-block text-xs font-semibold text-green-600 bg-green-500/10 px-2 py-1 rounded-lg">Credited ✓</span>
                  ) : isAutoClaiming ? (
                    <span className="mt-2 inline-block text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-lg animate-pulse">Crediting…</span>
                  ) : reached ? (
                    <span className="mt-2 inline-block text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-lg">Unlocked 🎉</span>
                  ) : (
                    <span className="mt-2 inline-block text-xs text-muted-foreground">{m.count - referredAgents.length} more needed</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Referred members */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3.5 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Your Team</h2>
            <span className="text-xs text-muted-foreground">{referredAgents.length} member{referredAgents.length !== 1 ? 's' : ''}</span>
          </div>
          {referredAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Users size={22} className="text-muted-foreground" /></div>
              <p className="text-sm font-medium text-foreground">No referrals yet</p>
              <p className="text-xs text-muted-foreground">Share your referral link to start building your team.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/30">
                  {['Member', 'Joined', 'Orders', 'Revenue', 'KYC', 'Status'].map(h => <th key={h} className="text-left px-4 py-3 font-semibold text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border">
                  {referredAgents.map(agent => (
                    <tr key={agent.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3"><p className="font-medium text-foreground">{agent.fullName}</p><p className="text-xs text-muted-foreground">{agent.email}</p></td>
                      <td className="px-4 py-3 text-foreground whitespace-nowrap">{formatDate(agent.createdAt)}</td>
                      <td className="px-4 py-3 text-foreground">{agent.totalOrders}</td>
                      <td className="px-4 py-3 font-semibold text-foreground">{formatCurrency(agent.dropserviceEarnings)}</td>
                      <td className="px-4 py-3">{kycBadge(agent.kycStatus)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${agent.suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{agent.suspended ? 'Suspended' : 'Active'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Commission history */}
        {affiliateTxns.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-4 sm:px-5 py-3.5 border-b border-border"><h2 className="text-sm font-semibold text-foreground">Commission History</h2></div>
            <div className="divide-y divide-border">
              {affiliateTxns.map(txn => (
                <div key={txn.id} className="flex items-center gap-3 px-4 sm:px-5 py-3.5">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0"><TrendingUp size={14} className="text-green-600" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm text-foreground truncate">{txn.description}</p><p className="text-xs text-muted-foreground">{formatDate(txn.createdAt)}</p></div>
                  <p className="text-sm font-bold text-green-600 flex-shrink-0">+{formatCurrency(txn.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
